import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The `FinancialResultsTypeFilter` function is a React component that renders a dropdown menu for
 * selecting a financial result type and updates the options based on the selected value.
 * @returns The FinancialResultsTypeFilter component is being returned.
 */
function FinancialResultsTypeFilter({ setFinancialsResultTypeOptions, FinancialsResultTypeOptions, customPathname, label = "Financials Result Type", isPopupOpen, setIsPopupOpen }) {
    const { FinancialResultType, refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.financialType?.map(value => value.id) ?? [];
        setFinancialsResultTypeOptions(FinancialResultType?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })));
    }, [FinancialResultType, customPathname, refineSearchFilters, setFinancialsResultTypeOptions]);

    const handleFinancialTypeChange = (e) => {
        const value = e.target.value;
        setFinancialsResultTypeOptions(FinancialsResultTypeOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.id === value })));
    };

    const selectedFinancialsResultType = FinancialsResultTypeOptions?.filter(option => option.isChecked)?.at(0);

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {FinancialResultType.slice(1,3).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedFinancialsResultType && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedFinancialsResultType.name}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4">
                        <h3 className="text-sm font-medium mb-3 text-gray-700">{label}</h3>
                        <div className="space-y-2">
                            <div className="max-h-60 overflow-y-auto space-y-2">
                                <label className="flex items-center space-x-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                        type="radio"
                                        name="financialResultType"
                                        value=""
                                        checked={!selectedFinancialsResultType}
                                        onChange={handleFinancialTypeChange}
                                        className="text-[#BC8B20] focus:ring-[#BC8B20]"
                                    />
                                    <span className="text-[13px] font-semibold">All</span>
                                </label>
                                <div className='grid grid-cols-2'>
                                {FinancialsResultTypeOptions && FinancialsResultTypeOptions.length > 0 ? (
                                    FinancialsResultTypeOptions.map((financialType, index) => (
                                        <label key={financialType.id} className="flex items-center space-x-2 hover:bg-gray-50 rounded cursor-pointer py-2">
                                            <input
                                                type="radio"
                                                name="financialResultType"
                                                value={financialType.id}
                                                checked={selectedFinancialsResultType?.id === financialType.id}
                                                onChange={handleFinancialTypeChange}
                                                className="text-[#BC8B20] focus:ring-[#BC8B20]"
                                            />
                                            <span className="text-[13px] font-semibold">{financialType.name}</span>
                                        </label>
                                    ))
                                ) : (
                                    <div className="text-[13px] text-gray-500 p-2">No results found!</div>
                                )}
                            </div>
                            </div>
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default FinancialResultsTypeFilter;